import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Building2,
  Calendar,
  CheckCircle2,
  Clock3,
  Crown,
  MapPinOff,
  RefreshCcw,
  ShieldAlert,
  Truck,
  Users,
  WalletCards,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL || "";

function getLimaDate() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Lima",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function formatMoney(value) {
  return `S/ ${Number(value || 0).toLocaleString("es-PE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function formatDateTime(value) {
  if (!value) return "Sin fecha";
  try {
    return new Intl.DateTimeFormat("es-PE", {
      dateStyle: "short",
      timeStyle: "short",
      timeZone: "America/Lima",
    }).format(new Date(value));
  } catch (_) {
    return value;
  }
}

function MetricCard({ icon: Icon, label, value, sublabel, tone = "primary" }) {
  const toneClasses = {
    primary: "bg-brand-primary/10 text-brand-primary",
    green: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
    slate: "bg-slate-100 text-slate-700",
    gold: "bg-brand-gold/20 text-amber-700",
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {label}
          </p>
          <p className="mt-2 text-2xl font-extrabold text-slate-950">{value}</p>
          {sublabel && <p className="mt-1 text-xs text-slate-500">{sublabel}</p>}
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${toneClasses[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, description, children, action }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-extrabold text-slate-950">{title}</h2>
          {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function AlertPill({ severity }) {
  const severityConfig = {
    high: "bg-red-100 text-red-700 border-red-200",
    medium: "bg-amber-100 text-amber-700 border-amber-200",
    low: "bg-emerald-100 text-emerald-700 border-emerald-200",
  };
  return (
    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase ${severityConfig[severity] || severityConfig.low}`}>
      {severity || "low"}
    </span>
  );
}

function EmptyState({ label }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center text-sm text-slate-500">
      {label}
    </div>
  );
}

function ActionQueue({ queueKey, queue, queues, onSelectQueue, onOpenAdminOperations }) {
  const queueEntries = Object.entries(queues || {}).filter(([, value]) => value);

  if (!queue) {
    return (
      <SectionCard
        title="Filtered action queue"
        description="Click an operational alert to open the exact queue that needs execution."
      >
        <EmptyState label="No hay una cola activa. Selecciona una alerta accionable." />
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Filtered action queue"
      description="Lista operacional filtrada desde la alerta seleccionada. Sirve para ejecutar, no solo observar."
      action={
        <button
          type="button"
          onClick={onOpenAdminOperations}
          className="rounded-xl bg-slate-950 px-3 py-2 text-xs font-bold text-white transition hover:bg-slate-800"
        >
          Abrir Admin Operations
        </button>
      }
    >
      <div className="mb-4 flex flex-wrap gap-2">
        {queueEntries.map(([key, value]) => (
          <button
            key={key}
            type="button"
            onClick={() => onSelectQueue(key)}
            className={`rounded-full border px-3 py-1 text-xs font-bold transition ${
              key === queueKey
                ? "border-brand-primary bg-brand-primary text-white"
                : "border-slate-200 bg-white text-slate-600 hover:border-brand-primary hover:text-brand-primary"
            }`}
          >
            {value.title} ({value.count || 0})
          </button>
        ))}
      </div>

      <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-black text-slate-950">{queue.title}</h3>
            <p className="text-sm text-slate-600">{queue.description}</p>
          </div>
          <span className="rounded-full bg-slate-950 px-3 py-1 text-xs font-bold text-white">
            {queue.count || 0} items
          </span>
        </div>
      </div>

      {(queue.items || []).length ? (
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <div className="hidden grid-cols-[90px_1.2fr_1.2fr_1fr_1fr_1fr] gap-3 bg-slate-100 px-4 py-2 text-xs font-black uppercase tracking-wide text-slate-500 lg:grid">
            <span>ID</span>
            <span>Cliente</span>
            <span>Tienda</span>
            <span>Distrito / estado</span>
            <span>Conductor</span>
            <span>COD</span>
          </div>
          <div className="divide-y divide-slate-200">
            {queue.items.map((item) => (
              <div
                key={`${queueKey}-${item.id}`}
                className="grid grid-cols-1 gap-3 bg-white px-4 py-3 text-sm lg:grid-cols-[90px_1.2fr_1.2fr_1fr_1fr_1fr]"
              >
                <div>
                  <p className="font-black text-slate-950">#{item.id}</p>
                  {item.priority && (
                    <span className="mt-1 inline-block rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">
                      PRIORITY
                    </span>
                  )}
                </div>
                <div>
                  <p className="font-bold text-slate-900">{item.recipient_name || "Sin receptor"}</p>
                  <p className="text-xs text-slate-500">{item.recipient_phone || "Sin teléfono"}</p>
                </div>
                <div>
                  <p className="font-bold text-slate-900">{item.merchant_name || "Sin tienda"}</p>
                  <p className="line-clamp-1 text-xs text-slate-500">{item.address || "Sin dirección"}</p>
                </div>
                <div>
                  <p className="font-bold text-slate-900">{item.district || "Sin distrito"}</p>
                  <p className="text-xs text-slate-500">
                    {item.raw_status || item.status || "Sin estado"} · {item.has_location ? "con ubicación" : "sin ubicación"}
                  </p>
                </div>
                <div>
                  <p className="font-bold text-slate-900">{item.driver_name || "Sin conductor"}</p>
                  <p className="text-xs text-slate-500">Slot {item.slot || "N/A"}</p>
                </div>
                <div>
                  <p className="font-bold text-slate-900">{formatMoney(item.charge)}</p>
                  <p className={`text-xs font-bold ${(item.cod_pending || 0) > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                    Pendiente {formatMoney(item.cod_pending)}
                  </p>
                </div>
              </div>
            ))}
          </div>
          {(queue.count || 0) > (queue.items || []).length && (
            <div className="bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-800">
              Mostrando los primeros {queue.items.length} de {queue.count}. Abre Admin Operations para gestionar el resto.
            </div>
          )}
        </div>
      ) : (
        <EmptyState label="Esta cola no tiene items para la fecha seleccionada." />
      )}
    </SectionCard>
  );
}

export default function MasterOperationalDashboard({ username, onLogout, setActiveComponent }) {
  const [selectedDate, setSelectedDate] = useState(getLimaDate());
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeQueueKey, setActiveQueueKey] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/master/operational-dashboard`, {
        params: { date: selectedDate },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setDashboard(response.data);
    } catch (err) {
      console.error("Error loading master operational dashboard:", err);
      setError(
        err?.response?.data?.error ||
          "No se pudo cargar el dashboard operacional master."
      );
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const lifecycleData = useMemo(
    () => (dashboard?.lifecycle || []).filter((item) => item.count > 0),
    [dashboard]
  );

  const summary = dashboard?.summary || {};
  const cod = dashboard?.cod || {};
  const settlement = cod?.settlement || {};
  const executive = dashboard?.executive || {};
  const actionQueues = dashboard?.action_queues || {};
  const activeQueue = activeQueueKey ? actionQueues[activeQueueKey] : null;

  useEffect(() => {
    if (!dashboard) return;
    const queues = dashboard.action_queues || {};
    if (activeQueueKey && queues[activeQueueKey]) return;

    const firstAlertQueue = (dashboard.alerts || []).find(
      (alert) => alert.queue_key && queues[alert.queue_key]?.count > 0
    )?.queue_key;
    const firstQueue = Object.entries(queues).find(([, queue]) => (queue?.count || 0) > 0)?.[0];
    setActiveQueueKey(firstAlertQueue || firstQueue || null);
  }, [dashboard, activeQueueKey]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-950 text-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setActiveComponent("MasterHome")}
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 transition hover:bg-white/20"
              aria-label="Volver al inicio master"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-gold/20">
              <Crown className="h-6 w-6 text-brand-gold" />
            </div>
            <div>
              <h1 className="text-2xl font-black">Master Operational Dashboard</h1>
              <p className="text-sm text-white/70">
                Control tower ejecutivo para {username || "Master"} · verdad operacional diaria
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-sm">
              <Calendar className="h-4 w-4 text-brand-gold" />
              <input
                type="date"
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
                className="bg-transparent text-white outline-none [color-scheme:dark]"
              />
            </label>
            <button
              type="button"
              onClick={fetchDashboard}
              className="flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-bold text-slate-950 transition hover:bg-slate-100"
            >
              <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Actualizar
            </button>
            {onLogout && (
              <button
                type="button"
                onClick={onLogout}
                className="rounded-xl bg-white/10 px-4 py-2 text-sm font-bold transition hover:bg-white/20"
              >
                Salir
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        <div className="mb-5 rounded-2xl border border-brand-gold/40 bg-amber-50 p-4 text-sm text-amber-900">
          <strong>Idea central:</strong> este dashboard debe responder en menos de 30 segundos:
          ¿cómo está la operación hoy, dónde está el riesgo, cuánto COD está expuesto,
          qué conductores/tiendas/distritos requieren acción y qué eventos explican la verdad?
        </div>

        {error && (
          <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
            {error}
          </div>
        )}

        {loading && !dashboard ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500 shadow-sm">
            Cargando inteligencia operacional...
          </div>
        ) : (
          <>
            <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-white">
                    <span className="text-xl font-black">
                      {executive.health_score ?? "--"}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-brand-primary">
                      Executive operational health
                    </p>
                    <h2 className="mt-1 text-xl font-black text-slate-950">
                      {executive.health_label || "Calculando salud operacional"}
                    </h2>
                    <p className="mt-2 max-w-3xl text-sm text-slate-600">
                      {executive.summary ||
                        "El dashboard convierte datos operativos diarios en una lectura ejecutiva accionable."}
                    </p>
                    {(executive.next_actions || []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {executive.next_actions.map((action) => (
                          <span
                            key={action}
                            className="rounded-full bg-brand-primary/10 px-3 py-1 text-xs font-bold text-brand-primary"
                          >
                            {action}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveComponent("AdminOperations")}
                  className="rounded-xl bg-slate-950 px-4 py-2 text-sm font-bold text-white transition hover:bg-slate-800"
                >
                  Abrir Admin Operations
                </button>
              </div>
            </section>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <MetricCard
                icon={Truck}
                label="Entregas del día"
                value={summary.total_deliveries || 0}
                sublabel={`${summary.unresolved || 0} abiertas · ${summary.in_progress || 0} en progreso`}
              />
              <MetricCard
                icon={CheckCircle2}
                label="Success rate"
                value={formatPercent(summary.success_rate)}
                sublabel={`${summary.delivered || 0} entregadas`}
                tone="green"
              />
              <MetricCard
                icon={ShieldAlert}
                label="Rejection rate"
                value={formatPercent(summary.rejection_rate)}
                sublabel={`${summary.rejected || 0} rechazadas`}
                tone={(summary.rejection_rate || 0) >= 10 ? "red" : "amber"}
              />
              <MetricCard
                icon={WalletCards}
                label="COD expuesto"
                value={formatMoney(cod.expected)}
                sublabel={`${formatMoney(cod.pending)} pendiente · ${formatPercent(cod.collection_rate)} cobrado`}
                tone="gold"
              />
              <MetricCard
                icon={Users}
                label="Conductores activos"
                value={summary.active_drivers || 0}
                sublabel={`${summary.assigned_deliveries || 0} entregas asignadas`}
                tone="slate"
              />
              <MetricCard
                icon={Building2}
                label="Tiendas activas"
                value={summary.active_merchants || 0}
                sublabel={`${summary.pickup_orders || 0} recojos programados`}
                tone="primary"
              />
              <MetricCard
                icon={AlertTriangle}
                label="Sin conductor"
                value={summary.unassigned_deliveries || 0}
                sublabel="Riesgo inmediato de ejecución"
                tone={(summary.unassigned_deliveries || 0) > 0 ? "red" : "green"}
              />
              <MetricCard
                icon={MapPinOff}
                label="Sin ubicación"
                value={summary.missing_locations || 0}
                sublabel="Calidad de datos para ruteo"
                tone={(summary.missing_locations || 0) > 0 ? "amber" : "green"}
              />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-5 xl:grid-cols-3">
              <div className="xl:col-span-2">
                <SectionCard
                  title="Delivery lifecycle"
                  description="Estado operativo diario: flujo de entregas, presión pendiente y puntos cerrados."
                >
                  {lifecycleData.length ? (
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={lifecycleData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                          <YAxis allowDecimals={false} />
                          <Tooltip
                            formatter={(value, _name, item) => [
                              `${value} (${item?.payload?.percentage || 0}%)`,
                              "Entregas",
                            ]}
                          />
                          <Bar dataKey="count" fill="#B1336E" radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <EmptyState label="No hay entregas registradas para esta fecha." />
                  )}
                </SectionCard>
              </div>

              <SectionCard
                title="Operational alerts"
                description="Acciones que protegen SLA, COD y ejecución."
              >
                <div className="space-y-3">
                  {(dashboard?.alerts || []).map((alert, index) => (
                    <button
                      key={`${alert.title}-${index}`}
                      type="button"
                      onClick={() => alert.queue_key && setActiveQueueKey(alert.queue_key)}
                      disabled={!alert.queue_key}
                      className={`w-full rounded-xl border p-3 text-left transition ${
                        alert.queue_key && alert.queue_key === activeQueueKey
                          ? "border-brand-primary bg-brand-primary/5 shadow-sm"
                          : "border-slate-200 bg-slate-50"
                      } ${alert.queue_key ? "hover:border-brand-primary hover:bg-white" : "cursor-default"}`}
                    >
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <h3 className="font-bold text-slate-900">{alert.title}</h3>
                        <AlertPill severity={alert.severity} />
                      </div>
                      <p className="text-sm text-slate-600">{alert.message}</p>
                      {alert.action && (
                        <p className="mt-2 text-xs font-bold text-brand-primary">
                          {alert.queue_key ? "Ver cola filtrada → " : ""}
                          {alert.action}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              </SectionCard>
            </div>

            <div className="mt-6">
              <ActionQueue
                queueKey={activeQueueKey}
                queue={activeQueue}
                queues={actionQueues}
                onSelectQueue={setActiveQueueKey}
                onOpenAdminOperations={() => setActiveComponent("AdminOperations")}
              />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-5 xl:grid-cols-3">
              <SectionCard
                title="Driver intelligence"
                description="Conductores con mayor carga, éxito, rechazo y COD registrado."
              >
                {(dashboard?.drivers || []).length ? (
                  <div className="space-y-3">
                    {dashboard.drivers.slice(0, 6).map((driver) => (
                      <div key={driver.id || driver.name} className="rounded-xl border border-slate-200 p-3">
                        <div className="flex items-center justify-between">
                          <p className="font-bold text-slate-900">{driver.name}</p>
                          <span className="text-xs font-bold text-slate-500">{driver.assigned} entregas</span>
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                          <span className="rounded-lg bg-emerald-50 px-2 py-1 text-emerald-700">
                            {formatPercent(driver.success_rate)} éxito
                          </span>
                          <span className="rounded-lg bg-red-50 px-2 py-1 text-red-700">
                            {formatPercent(driver.rejection_rate)} rechazo
                          </span>
                          <span className="rounded-lg bg-amber-50 px-2 py-1 text-amber-700">
                            {formatMoney(driver.cod_collected)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState label="Aún no hay conductores asignados para esta fecha." />
                )}
              </SectionCard>

              <SectionCard
                title="Merchant intelligence"
                description="Tiendas por volumen, éxito, rechazo y exposición COD."
              >
                {(dashboard?.merchants || []).length ? (
                  <div className="space-y-3">
                    {dashboard.merchants.slice(0, 6).map((merchant) => (
                      <div key={merchant.id || merchant.name} className="rounded-xl border border-slate-200 p-3">
                        <div className="flex items-center justify-between gap-3">
                          <p className="truncate font-bold text-slate-900">{merchant.name}</p>
                          <span className="text-xs font-bold text-slate-500">{merchant.total}</span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs">
                          <span className="rounded-lg bg-emerald-50 px-2 py-1 text-emerald-700">
                            {formatPercent(merchant.success_rate)} éxito
                          </span>
                          <span className="rounded-lg bg-red-50 px-2 py-1 text-red-700">
                            {formatPercent(merchant.rejection_rate)} rechazo
                          </span>
                          <span className="rounded-lg bg-slate-50 px-2 py-1 text-slate-600">
                            {formatMoney(merchant.cod_expected)} COD
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState label="No hay tiendas activas para esta fecha." />
                )}
              </SectionCard>

              <SectionCard
                title="District risk"
                description="Distritos con rechazo, pendientes o concentración operacional."
              >
                {(dashboard?.districts || []).length ? (
                  <div className="space-y-3">
                    {dashboard.districts.slice(0, 6).map((district) => (
                      <div key={district.name} className="rounded-xl border border-slate-200 p-3">
                        <div className="flex items-center justify-between">
                          <p className="font-bold text-slate-900">{district.name}</p>
                          <span className="text-xs font-bold text-slate-500">{district.total} entregas</span>
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                          <span className="rounded-lg bg-emerald-50 px-2 py-1 text-emerald-700">
                            {district.delivered} OK
                          </span>
                          <span className="rounded-lg bg-red-50 px-2 py-1 text-red-700">
                            {district.rejected} rechazo
                          </span>
                          <span className="rounded-lg bg-amber-50 px-2 py-1 text-amber-700">
                            {district.pending} abiertas
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState label="No hay concentración distrital para esta fecha." />
                )}
              </SectionCard>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-5 xl:grid-cols-2">
              <SectionCard
                title="COD & settlement truth"
                description="Separación entre cobro registrado en entregas y conciliación financiera."
              >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <MetricCard icon={WalletCards} label="COD esperado" value={formatMoney(cod.expected)} tone="gold" />
                  <MetricCard icon={CheckCircle2} label="COD registrado" value={formatMoney(cod.collected)} tone="green" />
                  <MetricCard icon={Clock3} label="COD pendiente" value={formatMoney(cod.pending)} tone={(cod.pending || 0) > 0 ? "amber" : "green"} />
                  <MetricCard icon={BarChart3} label="Debe en pagos" value={formatMoney(settlement.debe)} sublabel={`${settlement.pending_records || 0} registros pendientes`} tone={(settlement.debe || 0) > 0 ? "red" : "slate"} />
                </div>
              </SectionCard>

              <SectionCard
                title="Operational event feed"
                description="Eventos estructurados del día para auditabilidad y futuro entrenamiento AI."
              >
                {(dashboard?.recent_events || []).length ? (
                  <div className="max-h-96 space-y-3 overflow-auto pr-1">
                    {dashboard.recent_events.map((event) => (
                      <div key={event.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-bold text-slate-900">{event.event_type}</p>
                          <span className="text-xs text-slate-500">{formatDateTime(event.created_at)}</span>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          {event.entity_type} #{event.entity_id || "N/A"} · source: {event.source || "backend"}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState label="No hay eventos operacionales estructurados para esta fecha." />
                )}
              </SectionCard>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600 shadow-sm">
              <h2 className="font-extrabold text-slate-950">Supuestos de datos visibles</h2>
              <ul className="mt-3 list-disc space-y-1 pl-5">
                {(dashboard?.data_assumptions || []).map((assumption) => (
                  <li key={assumption}>{assumption}</li>
                ))}
              </ul>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
